// =====================================================================
// Detecção de singularidades para anotar gráficos (CHARTS_STUDY §8).
// Puro: opera pela FORMA dos dados (medida × eixo categórico/temporal), então
// os mesmos detectores valem para QUALQUER view. Produz uma lista curta de
// "achados" que a UI mostra como selos sobre o gráfico.
// =====================================================================
import { toNumber, type ChartSpec } from "./charts.js";

export type InsightKind = "missing" | "outlier" | "concentration" | "spike" | "gap" | "trend";
export type InsightSeverity = "info" | "warning" | "critical";

export interface ChartInsight {
  kind: InsightKind;
  severity: InsightSeverity;
  text: string;
  /** categoria/ponto âncora (para destacar no gráfico, quando aplicável). */
  at?: string;
}

const pct = (n: number) => `${Math.round(n * 100)}%`;
const compact = (n: number) =>
  Math.abs(n) >= 1000 ? `${(n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k` : `${Math.round(n)}`;

function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

// Escolhe a medida principal do spec (a que o gráfico realmente plota).
function primaryMeasure(spec: ChartSpec, columns: string[]): string | undefined {
  if (spec.measure) return spec.measure;
  const numericLike = spec.series.find((s) => s !== "__count__");
  if (numericLike) return numericLike;
  return columns.find((c) => /(frete|valor|peso|tarifa|n_|total|qtd|count|dias)/i.test(c));
}

/**
 * Analisa as linhas + spec e retorna achados de singularidade ordenados por
 * severidade. Não lança; retorna [] quando nada se destaca.
 */
export function detectInsights(
  spec: ChartSpec,
  rows: Record<string, unknown>[],
  columns: string[],
): ChartInsight[] {
  const out: ChartInsight[] = [];
  if (rows.length < 3) return out;
  const m = primaryMeasure(spec, columns);
  if (!m) return out;

  const nums = rows.map((r) => toNumber(r[m]));
  const present = nums.filter((v): v is number => v != null);

  // 1) VAZIO/ZERO — fração de linhas sem valor da medida (lookup falhou etc.).
  const zeroOrNull = nums.filter((v) => v == null || v === 0).length;
  const zeroRatio = zeroOrNull / nums.length;
  if (zeroRatio >= 0.15) {
    out.push({
      kind: "missing",
      severity: zeroRatio >= 0.3 ? "critical" : "warning",
      text: `${pct(zeroRatio)} das linhas com ${m} zerado/ausente (${zeroOrNull} de ${nums.length}).`,
    });
  }

  // 2) OUTLIER — valor máximo muito acima da mediana (cauda longa / erro).
  if (present.length >= 5) {
    const med = median(present.filter((v) => v > 0));
    const max = Math.max(...present);
    if (med > 0 && max >= med * 3) {
      const ratio = (max / med).toFixed(1);
      const xKey = spec.x ?? spec.groupBy;
      const top = xKey ? rows.find((r) => toNumber(r[m]) === max)?.[xKey] : undefined;
      out.push({
        kind: "outlier",
        severity: "info",
        text: `${m} máximo (${compact(max)}) é ${ratio}× a mediana${top ? ` — destaque: ${top}` : ""}.`,
        at: top != null ? String(top) : undefined,
      });
    }
  }

  // 3) CONCENTRAÇÃO (Pareto) — poucas categorias dominam o total.
  const catKey = spec.groupBy ?? spec.x;
  const isTemporal = !!spec.x && /(competencia|data|m[êe]s|periodo)/i.test(spec.x);
  if (catKey && !isTemporal) {
    const agg = new Map<string, number>();
    for (const r of rows) {
      const k = String(r[catKey] ?? "—");
      agg.set(k, (agg.get(k) ?? 0) + (toNumber(r[m]) ?? 0));
    }
    const sorted = [...agg.values()].filter((v) => v > 0).sort((a, b) => b - a);
    const total = sorted.reduce((s, v) => s + v, 0);
    if (sorted.length >= 4 && total > 0) {
      const top2 = (sorted[0]! + (sorted[1] ?? 0)) / total;
      if (top2 >= 0.5) {
        out.push({
          kind: "concentration",
          severity: "info",
          text: `Concentração: as 2 maiores categorias somam ${pct(top2)} do total de ${m} (${sorted.length} categorias).`,
        });
      }
    }
  }

  // 4) TEMPORAL — gap (ponto zerado entre não-zeros) e pico/queda no último ponto.
  if (spec.x && isTemporal) {
    const series = rows
      .map((r) => ({ x: String(r[spec.x!] ?? ""), v: toNumber(r[m]) ?? 0 }))
      .filter((p) => p.x)
      .sort((a, b) => a.x.localeCompare(b.x));
    // gap: zero cercado por valores positivos
    for (let i = 1; i < series.length - 1; i++) {
      if (series[i]!.v === 0 && series[i - 1]!.v > 0 && series[i + 1]!.v > 0) {
        out.push({ kind: "gap", severity: "warning", text: `${series[i]!.x.slice(0, 7)} sem ${m} (vazio entre meses com dados).`, at: series[i]!.x });
      }
    }
    // pico/queda: último ponto vs anterior
    if (series.length >= 2) {
      const last = series[series.length - 1]!;
      const prev = series[series.length - 2]!;
      if (prev.v > 0) {
        const delta = (last.v - prev.v) / prev.v;
        if (Math.abs(delta) >= 0.4) {
          out.push({
            kind: "spike",
            severity: Math.abs(delta) >= 0.8 ? "warning" : "info",
            text: `${m} ${delta > 0 ? "subiu" : "caiu"} ${pct(Math.abs(delta))} em ${last.x.slice(0, 7)} vs. o mês anterior.`,
            at: last.x,
          });
        }
      }
    }
  }

  const order: Record<InsightSeverity, number> = { critical: 0, warning: 1, info: 2 };
  return out.sort((a, b) => order[a.severity] - order[b.severity]).slice(0, 4);
}
